import { getCombinedToken } from '@local/login';
import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/dist/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getIsProduction } from 'src/utils/get-is-production';

import type {
    AssignRoleArg,
    DatasetsPageResult,
    DatasetsQueryArg,
    LegacyLoginResponse,
    LegacyLoginRequest,
    Subscription,
    SubscriptionsLookupResponse,
    SubscriptionsPageResult,
    SubscriptionsQueryArg,
    SupportPackagesPageResult,
    SupportPackagesQueryArg,
    UpdateSubscriptionInput,
    UserResponse,
    UsersLookupResponse,
    UsersPageResult,
    UsersQueryArg,
    ValidateResponse,
    WorkspaceRole,
    WorkspaceRolesQueryArg,
    WorkspacesPageResult,
    WorkspacesQueryArg,
} from './queries';
import {
    assignRoleMutation,
    datasetsInfiniteQuery,
    legacyLoginMutation,
    signoutMutation,
    SUBSCRIPTION_TAG,
    subscriptionByIdQuery,
    subscriptionsInfiniteQuery,
    subscriptionsLookupQuery,
    supportPackagesInfiniteQuery,
    updateSubscriptionMutation,
    userQuery,
    usersInfiniteQuery,
    usersLookupQuery,
    validateQuery,
    workspaceRolesQuery,
    workspacesInfiniteQuery,
} from './queries';

export const getImagoMpApiUrl = () => {
    if (getIsProduction()) {
        return 'https://imago.api.seequent.com/';
    }

    return 'https://imago-ci.api.dev-sqnt.com/';
};

const baseQuery: BaseQueryFn<string | FetchArgs, unknown> = async (args, api, extraOptions) => {
    const rawBaseQuery = fetchBaseQuery({
        baseUrl: getImagoMpApiUrl(),
        prepareHeaders: (headers) => {
            const combinedToken = getCombinedToken();
            headers.set('imago-api-token', combinedToken?.access_token ?? '');
            headers.set('Content-Type', 'application/json; charset=utf-8');
            return headers;
        },
    });

    return rawBaseQuery(args, api, extraOptions);
};

export const imagoMpApi = createApi({
    reducerPath: 'imagoMpApi',
    baseQuery,
    tagTypes: [SUBSCRIPTION_TAG],
    endpoints: (builder) => ({
        legacyLogin: builder.mutation<LegacyLoginResponse, LegacyLoginRequest>(legacyLoginMutation),
        validate: builder.query<ValidateResponse, undefined>(validateQuery),
        getUser: builder.query<UserResponse, string>(userQuery),
        getSubscriptionsLookup: builder.query<SubscriptionsLookupResponse, void>(
            subscriptionsLookupQuery,
        ),
        getSubscriptions: builder.infiniteQuery<
            SubscriptionsPageResult,
            SubscriptionsQueryArg,
            { offset: number }
        >(subscriptionsInfiniteQuery),
        getSubscriptionById: builder.query<Subscription, string>(subscriptionByIdQuery),
        updateSubscription: builder.mutation<Subscription, UpdateSubscriptionInput>(
            updateSubscriptionMutation,
        ),
        getUsersLookup: builder.query<UsersLookupResponse, void>(usersLookupQuery),
        getUsers: builder.infiniteQuery<UsersPageResult, UsersQueryArg, { offset: number }>(
            usersInfiniteQuery,
        ),
        getWorkspaces: builder.infiniteQuery<
            WorkspacesPageResult,
            WorkspacesQueryArg,
            { offset: number }
        >(workspacesInfiniteQuery),
        getDatasets: builder.infiniteQuery<
            DatasetsPageResult,
            DatasetsQueryArg,
            { offset: number }
        >(datasetsInfiniteQuery),
        getSupportPackages: builder.infiniteQuery<
            SupportPackagesPageResult,
            SupportPackagesQueryArg,
            { offset: number }
        >(supportPackagesInfiniteQuery),
        signout: builder.mutation<void, void>(signoutMutation),
        getWorkspaceRoles: builder.query<WorkspaceRole[], WorkspaceRolesQueryArg>(
            workspaceRolesQuery,
        ),
        assignRole: builder.mutation<WorkspaceRole[], AssignRoleArg>(assignRoleMutation),
    }),
});

export const {
    useLegacyLoginMutation,
    useValidateQuery,
    useGetUserQuery,
    useGetSubscriptionsLookupQuery,
    useGetSubscriptionsInfiniteQuery,
    useGetSubscriptionByIdQuery,
    useUpdateSubscriptionMutation,
    useGetUsersLookupQuery,
    useGetUsersInfiniteQuery,
    useGetWorkspacesInfiniteQuery,
    useGetDatasetsInfiniteQuery,
    useGetSupportPackagesInfiniteQuery,
    useSignoutMutation,
    useGetWorkspaceRolesQuery,
    useAssignRoleMutation,
} = imagoMpApi;
